import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../theme";
import { buildLevelStatsRows } from "./levelStatsUtils";
import AbilityBadge from "./AbilityBadge";

/**
 * Zeigt die Kartenwerte (Schaden, Leben, Rüstung, Speed, Fähigkeiten) und die
 * dafür benötigte kumulierte BCX-Anzahl pro Level - analog zur "STATS"-Ansicht
 * auf splinterlands.com.
 */
const LevelStatsTable = ({ card, details, highlightLevel = null }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const edition = card?.edition ?? details?.edition ?? parseInt(String(details?.editions || '').split(',')[0], 10);
  const rows = buildLevelStatsRows(details?.stats, details?.rarity, edition);

  if (!rows.length) {
    return (
      <Box sx={{ pt: 3, px: 3, pb: 3 }}>
        <Typography color={colors.grey[300]}>
          Für diese Karte sind keine Level-Statistiken verfügbar.
        </Typography>
      </Box>
    );
  }

  // Spalten dynamisch ein-/ausblenden, je nachdem welche Werte die Karte überhaupt nutzt
  // (z.B. hat ein reiner Ranged-Attacker keine Angriffs- oder Magie-Werte)
  const showAttack = rows.some(r => r.attack > 0);
  const showRanged = rows.some(r => r.ranged > 0);
  const showMagic = rows.some(r => r.magic > 0);
  const showArmor = rows.some(r => r.armor > 0);
  const hasCards = rows.some(r => r.cards !== null && r.cards !== undefined);
  // Mana ist bei Splinterlands-Karten level-unabhängig fix, braucht also keine eigene Spalte
  const mana = rows[0]?.mana;

  return (
    <Box sx={{ pt: 3, px: 3, pb: 3 }}>
      <Box display="flex" alignItems="baseline" gap={1.5} mb={0.5}>
        <Typography variant="h6" fontWeight="bold">
          Werte pro Level
        </Typography>
        {mana > 0 && (
          <Typography variant="body2" color={colors.grey[300]}>
            Mana: <strong>{mana}</strong>
          </Typography>
        )}
      </Box>
      <Typography variant="caption" color={colors.grey[400]} sx={{ display: 'block', mb: 2 }}>
        Fähigkeiten kumulieren über Level - ausgegraut = bereits auf einem niedrigeren Level freigeschaltet, hervorgehoben = neu ab diesem Level.
      </Typography>
      <TableContainer component={Paper} sx={{ bgcolor: colors.primary[400], maxHeight: 500 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: colors.blueAccent[700] }}>Level</TableCell>
              {hasCards && <TableCell sx={{ fontWeight: 'bold', bgcolor: colors.blueAccent[700] }}>Cards (BCX)</TableCell>}
              {showAttack && <TableCell sx={{ fontWeight: 'bold', bgcolor: colors.blueAccent[700] }}>Melee</TableCell>}
              {showRanged && <TableCell sx={{ fontWeight: 'bold', bgcolor: colors.blueAccent[700] }}>Ranged</TableCell>}
              {showMagic && <TableCell sx={{ fontWeight: 'bold', bgcolor: colors.blueAccent[700] }}>Magic</TableCell>}
              {showArmor && <TableCell sx={{ fontWeight: 'bold', bgcolor: colors.blueAccent[700] }}>Armor</TableCell>}
              <TableCell sx={{ fontWeight: 'bold', bgcolor: colors.blueAccent[700] }}>Health</TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: colors.blueAccent[700] }}>Speed</TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: colors.blueAccent[700] }}>Abilities</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.level}
                sx={{
                  bgcolor: row.level === highlightLevel ? `${colors.greenAccent[700]}40` : 'transparent',
                  '&:hover': { bgcolor: colors.primary[500] }
                }}
              >
                <TableCell>
                  <Typography fontWeight="bold" color={colors.greenAccent[500]}>{row.level}</Typography>
                </TableCell>
                {hasCards && (
                  <TableCell>{row.cards !== null && row.cards !== undefined ? row.cards.toLocaleString('en-US') : '-'}</TableCell>
                )}
                {showAttack && <TableCell>{row.attack}</TableCell>}
                {showRanged && <TableCell>{row.ranged}</TableCell>}
                {showMagic && <TableCell>{row.magic}</TableCell>}
                {showArmor && <TableCell>{row.armor}</TableCell>}
                <TableCell>{row.health}</TableCell>
                <TableCell>{row.speed}</TableCell>
                <TableCell>
                  {row.abilities.length > 0 ? (
                    <Box display="flex" gap={0.75} flexWrap="wrap" alignItems="center">
                      {row.abilities.map((ability) => (
                        <AbilityBadge
                          key={ability}
                          ability={ability}
                          dimmed={!row.newAbilities.includes(ability)}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography color={colors.grey[500]}>-</Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {!hasCards && (
        <Typography variant="caption" color={colors.grey[400]} sx={{ display: 'block', mt: 1 }}>
          Für diese Edition liegt keine BCX-Tabelle vor - es werden nur die Kampfwerte angezeigt.
        </Typography>
      )}
    </Box>
  );
};

export default LevelStatsTable;
